import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextField, AreaField, SelectField } from "@/components/forms/Field";
import { AddressField } from "@/components/forms/AddressField";
import { FileDropzone } from "@/components/forms/FileDropzone";
import api, { formatApiError } from "@/lib/api";
import { LenderMatch } from "@/components/site/LenderMatch";
import { toast } from "sonner";

const TOP_KEYS = ["applicant_name", "email", "phone", "company", "property_address", "property_state"];

export function FormRunner({ type, fields, uploads = [], compute, successMessage, submitLabel = "Submit Request", testIdPrefix = "form", intro }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setUpload = (key, arr) => setFiles((f) => ({ ...f, [key]: arr }));

  const computedRows = useMemo(() => (compute ? compute(form) : []), [compute, form]);

  const submit = async () => {
    for (const fl of fields) {
      if (fl.required && !form[fl.name]) {
        toast.error(`Please fill in: ${fl.label}`);
        return;
      }
    }
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    const data = {};
    Object.keys(form).forEach((k) => { if (!TOP_KEYS.includes(k)) data[k] = form[k]; });
    computedRows.forEach((r) => { if (r.store) data[r.store] = r.value; });
    const documents = [];
    uploads.forEach((u) => (files[u.key] || []).forEach((d) => documents.push({ ...d, category: u.category })));
    const payload = {
      type,
      applicant_name: form.applicant_name || "",
      email: form.email,
      phone: form.phone || "",
      company: form.company || "",
      property_address: form.property_address || "",
      property_state: form.property_state || "",
      data,
      documents,
      referral_code: localStorage.getItem("bullseye_ref") || null,
    };
    try {
      const { data: res } = await api.post("/submissions", payload);
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-10" data-testid={`${testIdPrefix}-success`}>
        <div className="w-16 h-16 rounded-full bg-ok/15 grid place-items-center mx-auto">
          <CheckCircle2 className="w-9 h-9 text-ok" />
        </div>
        <h3 className="h-display text-2xl sm:text-3xl text-white mt-6">Request Submitted</h3>
        <p className="mt-3 text-dim leading-relaxed">{successMessage}</p>
        <div className="mt-6 inline-flex flex-col items-center rounded-xl border border-edge bg-surface/60 px-6 py-4">
          <span className="eyebrow">Reference</span>
          <span className="font-mono text-gold text-lg mt-1" data-testid={`${testIdPrefix}-reference`}>{done.reference}</span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/portal")} className="btn-gold rounded-full h-12 px-6 gap-2" data-testid={`${testIdPrefix}-track-cta`}>
            Track in Portal <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="btn-outline-glass rounded-full h-12 px-6">Back to Home</Button>
        </div>
        {done.lender_match?.best_match && (
          <div className="mt-10 text-left">
            <LenderMatch data={done.lender_match} variant="borrower" />
            <p className="mt-3 text-xs text-faint text-center">Sign in to your portal to request this lender and download your term sheet.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-form`}>
      {intro}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {fields.map((fl) => {
          const cls = fl.full ? "sm:col-span-2" : "";
          if (fl.name === "property_address")
            return <AddressField key={fl.name} className={cls} label={fl.label} value={form[fl.name]} onChange={(v) => onChange(fl.name, v)} onSelect={(a) => a.state && onChange("property_state", a.state)} testId={`${testIdPrefix}-${fl.name}`} />;
          if (fl.type === "select")
            return <SelectField key={fl.name} className={cls} label={fl.label} name={fl.name} value={form[fl.name]} onChange={onChange} options={fl.options} required={fl.required} testId={`${testIdPrefix}-${fl.name}`} />;
          if (fl.type === "textarea")
            return <AreaField key={fl.name} className="sm:col-span-2" label={fl.label} name={fl.name} value={form[fl.name]} onChange={onChange} testId={`${testIdPrefix}-${fl.name}`} />;
          return <TextField key={fl.name} className={cls} label={fl.label} name={fl.name} value={form[fl.name]} onChange={onChange} type={fl.type || "text"} required={fl.required} testId={`${testIdPrefix}-${fl.name}`} placeholder={fl.placeholder} />;
        })}
      </div>

      {computedRows.length > 0 && (
        <div className="mt-6 rounded-xl border border-gold/30 bg-gold/5 p-5" data-testid={`${testIdPrefix}-computed`}>
          {computedRows.map((r) => (
            <div key={r.label} className="flex items-center justify-between">
              <span className="text-sm text-dim">{r.label} <span className="text-xs text-faint">(estimated)</span></span>
              <span className="font-mono text-gold text-lg" data-testid={`${testIdPrefix}-computed-${r.store || "value"}`}>{r.display}</span>
            </div>
          ))}
        </div>
      )}

      {uploads.length > 0 && (
        <div className="mt-6 space-y-5">
          {uploads.map((u) => (
            <FileDropzone key={u.key} label={u.label} category={u.category} multiple={u.multiple !== false}
              value={files[u.key] || []} onChange={(arr) => setUpload(u.key, arr)} testId={`${testIdPrefix}-upload-${u.key}`} />
          ))}
        </div>
      )}

      <Button onClick={submit} disabled={submitting} className="btn-gold rounded-full h-14 px-8 mt-8 w-full sm:w-auto gap-2" data-testid={`${testIdPrefix}-submit`}>
        {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <>{submitLabel} <ArrowRight className="w-5 h-5" /></>}
      </Button>
      <p className="mt-4 text-xs text-faint max-w-lg">Submitting this request does not constitute an approval, commitment, or offer of financing. All financing is subject to underwriting, approval, documentation, availability, and applicable law.</p>
    </div>
  );
}
